<template>
    <div class="prize-pool-card">
        <!-- Header -->
        <!--<h2>{{ $t('prizePool') || 'Prize Pool' }}</h2>
-->
        <!-- Circular Progress Section -->
        <div class="circular-progress-section">
            <!-- Next Increment Badge - positioned above circle -->
            <div class="next-increment-badge" v-if="prizePool.nextIncrementIn > 0">
                <span>{{ prizePool.nextIncrementIn }} {{ $t('participantsToNext') || 'participants to next' }} +${{
                    prizePool.incrementAmount.toLocaleString() }}</span>
            </div>

            <!-- SVG Circular Progress -->
            <div class="circular-progress-container">
                <svg class="progress-ring" width="200" height="200">
                    <!-- Background circle -->
                    <circle class="progress-ring-bg" stroke="#e0e0e0" stroke-width="12" fill="transparent" r="90"
                        cx="100" cy="100" />
                    <!-- Progress circle -->
                    <circle class="progress-ring-circle" :stroke="organizerColor" stroke-width="12" fill="transparent"
                        r="90" cx="100" cy="100" :style="{ strokeDashoffset: progressOffset }" />
                </svg>

                <!-- Center content -->
                <div class="center-content">
                    <div class="participant-badge">
                        <i class="fa-regular fa-users"></i>
                        <span style="font-size: 14px !important;">{{ participantCount.toLocaleString() }} {{ $t('participants') || 'Participants' }}</span>
                    </div>
                    <h1 class="current-amount">${{ prizePool.computedCurrentAmount.toLocaleString() }}</h1>
                    <div><span style="font-size: 14px !important;">{{ $t('basePrizePool') || 'Base' }}: ${{
                        prizePool.baseAmount.toLocaleString() }}</span></div>
                </div>
            </div>

            <!-- Next Goal -->
            <div class="next-goal" v-if="prizePool.nextIncrementIn > 0">
                <h3><i class="fa-regular fa-flag mr-2"></i>{{ $t('nextIncrement') || 'Next Goal' }}: ${{
                    (prizePool.computedCurrentAmount + prizePool.incrementAmount).toLocaleString() }}</h3>
            </div>
        </div>

        <!-- Invite Button -->
        <button class="invite-btn" @click="$emit('invite')" :style="organizerColorStyle">
            <i class="fa-regular fa-user-plus mr-2"></i>
            {{ $t('inviteParticipants') || 'Invite participants' }}
        </button>
    </div>
</template>

<script>
export default {
    name: 'TournamentPrizePoolCard',
    props: {
        prizePool: {
            type: Object,
            required: true
        },
        participantCount: {
            type: Number,
            required: true
        }
    },
    computed: {
        progressPercentage() {
            if (this.prizePool.incrementEveryNRegs === 0) return 0;
            const currentProgress = this.prizePool.incrementEveryNRegs - this.prizePool.nextIncrementIn;
            return (currentProgress / this.prizePool.incrementEveryNRegs) * 100;
        },
        progressOffset() {
            const circumference = 2 * Math.PI * 90; // radius = 90
            const offset = circumference - (this.progressPercentage / 100) * circumference;
            return offset;
        },
        organizerColor() {
            if (this.$store.state.tournament?.tournamentSettings?.colors?.primary) {
                const color = this.$store.state.tournament.tournamentSettings.colors.primary;
                return color.startsWith('#') ? color : `#${color}`;
            }
            return '#009F63'; // Default green
        },
        organizerColorStyle() {
            return {
                backgroundColor: this.organizerColor,
                borderColor: this.organizerColor
            };
        }
    }
};
</script>

<style scoped lang="scss">
.prize-pool-card {
    text-align: center;
    border: 1px solid #9a9a9a;
    border-radius: 15px;
    background-color: var(--v-fullWhite-base);
    font-family: "Avenir", sans-serif;
    display: flex;
    flex-direction: column;
    padding: 24px 30px;
    justify-content: center;
    margin-left: auto;

    h2 {
        font-family: 'Avenir', sans-serif !important;
        font-weight: bold;
        font-size: 24px;
        margin-bottom: 24px;
    }

    h3 {
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.theme--dark .prize-pool-card {
    background-color: var(--v-cardBackground-base);
    border-color: #555;
}

.circular-progress-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    margin-bottom: 24px;
}

.next-increment-badge {
    position: absolute;
    top: -8px;
    right: 10px;
    width: 130px;
    background-color: var(--v-mainWhite-base);
    border: 1px solid #9a9a9a;
    border-radius: 8px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    color: var(--v-mainBlack-base);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 10;
    opacity: 0;
    transform: translateY(-10px);
    transition: opacity 0.3s ease, transform 0.3s ease;
    pointer-events: none;
}

.circular-progress-section:hover .next-increment-badge {
    opacity: 1;
    transform: translateY(0);
}

.theme--dark .next-increment-badge {
    background-color: var(--v-cardBackground-base);
    border-color: #555;
}

.circular-progress-container {
    position: relative;
    width: 200px;
    height: 200px;
    margin: 0 0 20px 0;
    animation: scaleIn 0.6s ease-out;
}

@keyframes scaleIn {
    from {
        transform: scale(0.8);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

.progress-ring {
    transform: rotate(-90deg);
}

.progress-ring-circle {
    transition: stroke-dashoffset 0.5s ease;
    stroke-dasharray: 565.48;
    /* 2 * PI * 90 */
    stroke-linecap: round;
    animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.8;
    }
}

.progress-ring-bg {
    opacity: 0.2;
}

.theme--dark .progress-ring-bg {
    stroke: #555;
}

.center-content {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    width: 160px;

    .participant-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--v-mainBlack-base);
        margin-bottom: 8px;
        span{
            margin-left: 5px;
        }
    }

    .current-amount {
        margin-bottom: 4px;
    }

    .base-label {
        font-size: 14px;
        color: var(--v-mainBlack-base);
    }
}

.next-goal {
    margin-bottom: 8px;

    h3 {
        margin: 0;
    }
}

.invite-btn {
    width: 100%;
    height: 36px;
    padding: 0 24px;
    background-color: #009F63;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "Avenir", sans-serif;

    &:hover {
        opacity: 0.9;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 159, 99, 0.3);
    }

    &:active {
        transform: translateY(0);
    }

    i {
        font-size: 14px;
    }
}
</style>
